/**
 * The meeting agenda, shared word for word by the two meetings that build one: the evaluation
 * meeting and the thematic meeting. It has its own namespace because it belongs to neither.
 */
export default {
    title: 'Meeting agenda with discussion points',
    description:
        'Organize your meeting timeline with key discussion points for each segment. On your meeting day, fill in the employee response for better tracking.',
    totalMeetingTime: 'Total meeting time:',
    minutes: '{{count}} minutes',
    aiBannerTitle: 'Use Amélio AI to generate your meeting agenda.',
    aiBannerDescription: 'Automatically generate a structured plan tailored to the employee.',
    generateMeetingPlan: 'Generate meeting plan',
    generatingTitle: 'Generating your meeting plan',
    generatingMessage:
        'Amélio AI is analyzing the evaluation data. This may take a few moments, please wait.',
    openingStatement: 'Opening Statement',
    openingStatementAiHint: 'We offered a foundation, now you can make it your own.',
    openingStatementPlaceholder: 'Write the opening statement...',
    openHandout: 'Open handout',
    addTopic: 'Add topic',
    newTopicPlaceholder: 'New topic',
    minutesShort: 'min',
    editAgendaItem: 'Edit agenda item',
    toggleAgendaItem: 'Show or hide discussion points',
    editOpeningStatement: 'Edit opening statement',
    confirmTopic: 'Save topic',
    cancelTopicEdit: 'Cancel topic edit',
    addDiscussionPoint: 'Add a meeting point',
    noDiscussionPoints: 'No discussion points yet.',
    readOnly: {
        noOpeningStatement: 'No opening statement was prepared.',
        noTopics: 'No topics were on the agenda.',
        noDiscussionPoints: 'No discussion points were noted for this topic.',
        noResponse: 'No response was noted.',
    },
    detailsAdded: 'Details added',
    employeeResponseLabel: 'Employee Response and notes',
    employeeResponsePlaceholder: "Record the employee's response here...",
    editEmployeeResponse: "Edit the employee's response",
    reorderTopic: 'Move the topic',
    reorderDiscussionPoint: 'Move the discussion point',
    done: 'Done',
    editDiscussionPoint: 'Edit Discussion Point',
    deleteDiscussionPoint: 'Delete discussion point',
    deleteTopicNamed: 'Delete the topic {{title}}',
    deleteTopicConfirm: {
        title: 'Delete this topic?',
        message: 'The topic and its discussion points will be removed from the meeting plan.',
        messageWithAnswers:
            "The topic, its discussion points and the employee's recorded answers will be removed from the meeting plan.",
        confirm: 'Delete topic',
    },
    dateAdded: 'Date added {{date}}',
    topicLabel: 'Topic',
    detailsAndContextLabel: 'Details & Context',
    saveChanges: 'Save Changes',
    generateError: "Couldn't generate the meeting plan. Please try again.",
    loadError: 'The meeting agenda could not be loaded. Reload the page to try again.',
    /** An agenda keeps the one language it was written in: said to a reader of the other language. */
    writtenInLanguage: {
        en: 'Plan written in English',
        fr: 'Plan written in French',
    },
    aiAgenda: {
        title: 'Generate a Meeting Plan with Amélio AI',
        step1: {
            question: 'Your meeting objective',
            placeholder:
                'Describe your objective...\nFor example: Congratulate on high performance, but inform that a new position is not immediately accessible.',
        },
        step2: {
            question: 'Context about employee performance',
            placeholder:
                'Are there specific aspects you would like to address in the meeting?\nFor example: Has particular difficulty staying motivated if the first version of the work is not accepted as is.',
            generateButton: 'Generate the plan',
        },
        sensitiveObjective: {
            message:
                'The plan cannot be generated while your objective mentions leave, health, human resources, the union or discipline. Rewrite or remove these sentences, then generate the plan again:',
        },
        /** A meeting topic's title refused: it is edited in the evaluation's meeting topics, not in the plan's window. */
        sensitiveTopic: {
            message:
                'The plan cannot be generated while a meeting topic mentions leave, health, human resources, the union or discipline. Edit it in the meeting topics of the evaluation, then generate the plan again.',
            item: 'Rewrite or remove this meeting topic: “{{title}}”',
        },
        integrate: {
            title: 'How would you like to integrate the plan?',
            addTitle: 'Add to agenda',
            addDescription: 'Keep existing items and add AI suggestions at the end',
            replaceTitle: 'Replace agenda',
            replaceDescription: 'Remove all current items and use only AI suggestions',
            /** Add is offered only into an agenda of the generated plan's language. */
            languageMismatch: {
                toEnglish:
                    'This plan is written in English and your agenda is in French. To switch the agenda to English, choose to replace it.',
                toFrench:
                    'This plan is written in French and your agenda is in English. To switch the agenda to French, choose to replace it.',
            },
        },
        replaceConfirm: {
            title: 'Confirm replacement',
            message:
                'The current meeting plan will be deleted and replaced by the new AI-generated plan. This action is irreversible.',
            question: 'Do you want to continue?',
            confirm: 'Yes, replace',
            cancel: 'Cancel',
        },
        overlay: {
            generating: 'Generating',
            message: 'AI is analyzing data and generating your personalized meeting plan',
        },
        imageAlt: 'AI-powered meeting plan illustration',
        /** What the AI wrote for the manager with a thematic meeting's plan: its summary and its alerts. */
        review: {
            summaryTitle: 'What the AI adapted',
            bannerTitle: 'Your plan was adapted to the context you wrote',
            alertsLabel: 'Points to watch',
            alertType: {
                meetingFit: 'Meeting fit',
                tension: 'Tension',
                boundary: 'Boundary',
            },
            dismiss: 'Dismiss',
        },
    },
};
